/**
 * @module PricePointHero
 * @description
 */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import merge from "lodash-es/merge";
import useContentProviderProduct from "@/content-provider/hooks/useContentProviderProduct";
import DynamicButton from "@/blocks/Buttons/DynamicButton";
import defaultProducts from "@/content-provider/utils/products";
import styles from "./HeroPricePoint.module.css";

const position = "HERO PRICE POINT";

/**
 * @function Component
 * @description Hero that displays a headline and a price point
 */
const Component = ({ ...props }) => {
  const {
    headline,
    hash = "fiberGig",
    background = "lady-using-laptop-couch-bg",
    location,
    useSelectAreasLegal,
  } = props;

  const [product, setProduct] = useState(null);
  const featured = useContentProviderProduct(hash);
  const backgroundPath = `${process.env.imagePath}/sections/hero/${background}`;

  /**
   * @function useEffect
   * @description
   */
  useEffect(() => {
    if (!featured) {
      setProduct({ ...defaultProducts["fiber-gig"] });
      return;
    }

    const productData = {
      hash,
      title: featured.title,
      name: featured.name,
      price: {
        base: `$${featured.price.base}`,
        promo: `$${featured.price.promo}`,
      },
      legal: {
        hash: featured.legal.hash,
        proximity: featured.legal.short,
      },
    };

    // Merge our default data with our featured product data.
    setProduct((defaultData) => merge({ ...defaultData }, { ...productData }));
  }, [hash, featured]);

  return (
    <section
      className={`component u-theme-dark ${styles.component}`}
      style={{ backgroundImage: `url(${backgroundPath}--large.jpg )` }}
      data-cypress="hero-price-point"
    >
      <div className={`l-container ${styles.component__wrapper}`}>
        <div className="g-row">
          <div className="g-col g-col--two-thirds">
            <div className={`${styles.component__contentWrapper}`}>
              <h2 className={`${styles.component__title} h1`}>{headline}</h2>

              <div className={styles.component__product}>
                {product !== null && Object.keys(product).length > 0 && (
                  <div
                    className={`product-${product.hash}`}
                    data-hash={product.hash}
                  >
                    <p className={`h5 ${styles.component__productTitle}`}>
                      {product.name}
                    </p>
                    <div className={styles.component__productInfo}>
                      <div className={styles.component__productPricing}>
                        <div className={styles.component__productPrice}>
                          <span
                            className="h1 h1--pricepoint"
                            data-cypress="price-point"
                          >
                            {product.price.promo}
                            <sup
                              className={styles.component__productPricingLegal}
                            >
                              <span data-legal={product.legal.hash} />
                            </sup>
                          </span>

                          <div className={styles.component__productLockup}>
                            <s className={styles.component__productPriceStrike}>
                              {product.price.base}
                            </s>
                          </div>
                        </div>
                      </div>
                      {/* <!-- .component__productPricing --> */}
                    </div>
                    {/* <!-- .component__productInfo --> */}

                    <p className={`legal ${styles.component__shortLegal}`}>
                      {product.legal.proximity}
                    </p>
                  </div>
                )}
              </div>

              <DynamicButton
                type="cart"
                position={position}
                location={location}
              />

              {useSelectAreasLegal && (
                <p className={styles.component__selectAreasLegal}>
                  Frontier<sup>&reg;</sup> Fiber Internet available in
                  select&nbsp;areas.
                </p>
              )}
            </div>
          </div>
        </div>
        <div
          className={styles.component__mobileBackground}
          style={{ backgroundImage: `url( ${backgroundPath}.jpg )` }}
        />
      </div>
    </section>
  );
};

Component.propTypes = {
  headline: PropTypes.node,
  hash: PropTypes.string,
  background: PropTypes.string,
  location: PropTypes.string,
  useSelectAreasLegal: PropTypes.bool,
};

export default Component;
